import { useState } from 'react'
import { FilePdf, FloppyDisk, Plus } from '@phosphor-icons/react'
import { istDateKey } from '@/lib/ist'
import { Input, Select } from '@/ui'
import { errorMessage, errorDetail } from '../../api/core'
import {
  useAttachLabReport,
  useLabReport,
  useLabReports,
  type AttachResult,
  type LabReportRow,
} from '../../api/quality'
import { useOrgs } from '../../api/reference'
import { useSamples } from '../../api/store'
import { ActionButton } from '../../components/Can'
import { UserName } from '../../components/bits'
import {
  BoardColumns,
  BoardPage,
  BoardTable,
  Counter,
  Counters,
  FactRow,
  Facts,
  Note,
  PanelActions,
  Pill,
  SidePanel,
  TwoLine,
  type BoardColumn,
} from '../../components/board'
import { useUrlState } from '../../lib/useUrlState'
import {
  csvRowCount,
  findBySeal,
  overallState,
  reportCounters,
  validateLabForm,
  type LabForm,
} from '../../model/labReport'
import { boardDate, boardWhen } from '../../model/payoutBoard'
import { BoardCard, BoardField } from '../packing/shared'
import { LabRowsTable } from './shared'

/**
 * Lab report (alignment map V1-F7-05a LabResult; F7.5): scan or type the sample seal, the lab is
 * the one contracted lab, type the report number and test date, attach the PDF as received and the
 * lab's results file; the system flags each row against the limit in force on the test date. No
 * second check in V1: the quality person who releases the lot reads this PDF before signing.
 */
export function LabResultsPage() {
  const [f, setF] = useUrlState({ open: '', search: '', page: 1 })
  const reports = useLabReports({ search: f.search || undefined, page: f.page, limit: 50 })
  const rows = reports.data?.rows ?? []
  const counters = reportCounters(
    rows.map((r) => ({
      overall: r.overall,
      status: r.status,
      rowsNotPass: r.rowsNotPass,
      enteredAt: r.receivedAt ?? r.testedAt,
    })),
  )
  const columns: BoardColumn<LabReportRow>[] = [
    {
      id: 'no',
      header: 'Report',
      cell: (r) => <TwoLine top={r.reportNo} sub={r.labName ?? undefined} />,
    },
    {
      id: 'unit',
      header: 'Batch · lot',
      cell: (r) => (
        <TwoLine
          top={r.unitCode}
          sub={r.sampleKind === 'LOT_RETAINED' ? 'gate sample' : 'QC sample'}
        />
      ),
    },
    { id: 'tested', header: 'Tested', hideBelow: 'sm', cell: (r) => boardDate(r.testedAt) },
    {
      id: 'rows',
      header: 'Rows',
      align: 'right',
      hideBelow: 'sm',
      cell: (r) => `${r.rows}${r.rowsNotPass ? ` · ${r.rowsNotPass} not pass` : ''}`,
    },
    {
      id: 'overall',
      header: 'Overall',
      cell: (r) => {
        const s = overallState(r.overall)
        return (
          <Pill tone={s.tone}>{r.status === 'SUPERSEDED' ? 'Replaced' : s.text.split(':')[0]}</Pill>
        )
      },
    },
  ]
  return (
    <BoardPage
      title={f.open ? 'Lab report' : 'Attach a lab report'}
      actions={
        f.open ? (
          <ActionButton
            perm="lab-reports:create"
            hide
            variant="secondary"
            leadingIcon={<Plus />}
            onClick={() => setF({ open: '' })}
          >
            Attach report
          </ActionButton>
        ) : undefined
      }
    >
      <Counters>
        <Counter label="Attached this week" value={counters.thisWeek} loading={reports.isLoading} />
        <Counter label="Incomplete" value={counters.incomplete} loading={reports.isLoading} />
        <Counter label="Not accepted" value={counters.notAccepted} loading={reports.isLoading} />
        <Counter
          label="Fail · held for re-test"
          value={counters.retest}
          loading={reports.isLoading}
        />
      </Counters>
      {f.open ? <ReportView id={f.open} /> : <AttachForm onSaved={(id) => setF({ open: id })} />}
      <BoardCard
        title="Reports"
        aside={
          <Input
            aria-label="Search report or batch"
            placeholder="Report number or batch code"
            value={f.search}
            onChange={(e) => setF({ search: e.target.value })}
            className="h-9 max-w-[260px]"
          />
        }
      >
        <BoardTable<LabReportRow>
          caption="Lab reports"
          columns={columns}
          rows={rows}
          getRowId={(r) => r.id}
          selectedId={f.open || null}
          onRowClick={(r) => setF({ open: r.id })}
          loading={reports.isLoading}
          error={reports.error ? errorMessage(reports.error) : undefined}
          empty="No lab reports yet."
          pager={
            reports.data
              ? {
                  page: f.page,
                  total: reports.data.total,
                  limit: reports.data.limit,
                  onPage: (page) => setF({ page }),
                }
              : undefined
          }
        />
        <Note>
          Incomplete: a required panel is missing. Not accepted: the lab’s accreditation had lapsed
          on the test date. A fail holds the farmer’s family of stock at once (F7-B1).
        </Note>
      </BoardCard>
    </BoardPage>
  )
}

function AttachForm({ onSaved }: { onSaved: (id: string) => void }) {
  const labs = useOrgs('LAB')
  const qc = useSamples({ kind: 'BATCH_QC', page: 1, limit: 200 })
  const gate = useSamples({ kind: 'LOT_RETAINED', page: 1, limit: 200 })
  const attach = useAttachLabReport()
  const lab = labs.data?.length === 1 ? labs.data[0] : null
  const [labPick, setLabPick] = useState('')
  const [form, setForm] = useState<Omit<LabForm, 'labOrgId'>>({
    seal: '',
    reportNo: '',
    testedAt: istDateKey(),
    pdf: null,
    results: null,
  })
  const [rowsLoaded, setRowsLoaded] = useState<number | null>(null)
  const [tried, setTried] = useState(false)
  const [result, setResult] = useState<AttachResult | null>(null)
  const [refusal, setRefusal] = useState<string[] | null>(null)
  const labOrgId = lab?.id ?? labPick
  const full: LabForm = { ...form, labOrgId }
  const errors = validateLabForm(full, istDateKey())
  const sample = findBySeal([...(qc.data?.rows ?? []), ...(gate.data?.rows ?? [])], form.seal)
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((x) => ({ ...x, [k]: v }))

  const save = async () => {
    setTried(true)
    setRefusal(null)
    if (Object.keys(errors).length || !form.pdf || !form.results) return
    try {
      const r = await attach.mutateAsync({
        sealPayload: form.seal,
        labOrgId,
        reportNo: form.reportNo,
        testedAt: form.testedAt,
        pdf: form.pdf,
        resultsFile: form.results,
      })
      setResult(r)
      onSaved(r.id)
    } catch (e) {
      const d = errorDetail(e)
      const errs = (d.errors ?? {}) as Record<string, unknown>
      const list = [errs.missing, errs.analytes, errs.problems].flatMap((x) =>
        Array.isArray(x) ? x.map(String) : [],
      )
      setRefusal(list.length ? list : null)
    }
  }
  const err = (k: keyof LabForm) => (tried ? errors[k] : undefined)

  return (
    <BoardColumns
      main={
        <BoardCard title="Report">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <BoardField
              label="Sample seal (scanned)"
              error={err('seal')}
              hint={
                sample
                  ? `${sample.kind === 'LOT_RETAINED' ? 'Lot' : 'Batch'} ${sample.unitCode ?? '—'} filled in`
                  : 'Scan the seal with the desk scanner, or type it.'
              }
            >
              <Input
                autoFocus
                value={form.seal}
                onChange={(e) => set('seal', e.target.value)}
                placeholder="QS-0001"
              />
            </BoardField>
            <BoardField
              label="Lab (preset)"
              error={err('labOrgId')}
              hint={lab ? 'The one contracted lab' : undefined}
            >
              {lab ? (
                <span className="flex h-10 items-center rounded-chip border-[1.5px] border-dashed border-[#8a827a] bg-paper-tint px-3">
                  {lab.name}
                </span>
              ) : (
                <Select
                  value={labPick}
                  onChange={setLabPick}
                  placeholder={
                    labs.isLoading
                      ? 'Loading…'
                      : (labs.data ?? []).length === 0
                        ? 'No lab set up'
                        : 'Pick the lab'
                  }
                  options={(labs.data ?? []).map((o) => ({ value: o.id, label: o.name }))}
                />
              )}
            </BoardField>
            <BoardField label="Report number" error={err('reportNo')}>
              <Input
                value={form.reportNo}
                onChange={(e) => set('reportNo', e.target.value)}
                placeholder="LR-2026-1108"
              />
            </BoardField>
            <BoardField label="Test date" error={err('testedAt')}>
              <Input
                type="date"
                value={form.testedAt}
                max={istDateKey()}
                onChange={(e) => set('testedAt', e.target.value)}
              />
            </BoardField>
            <BoardField label="PDF, as received" error={err('pdf')} hint={form.pdf?.name}>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="text-[14px]"
                onChange={(e) => set('pdf', e.target.files?.[0] ?? null)}
              />
            </BoardField>
            <BoardField
              label="Results file"
              error={err('results')}
              hint={
                form.results
                  ? `${form.results.name}${rowsLoaded !== null ? ` · ${rowsLoaded} rows loaded` : ''}`
                  : 'CSV or spreadsheet from the lab'
              }
            >
              <input
                type="file"
                accept=".csv,.xlsx,.xls,text/csv"
                className="text-[14px]"
                onChange={async (e) => {
                  const file = e.target.files?.[0] ?? null
                  set('results', file)
                  setRowsLoaded(
                    file && /\.csv$/i.test(file.name) ? csvRowCount(await file.text()) : null,
                  )
                }}
              />
            </BoardField>
          </div>
          {sample && form.seal && (
            <Pill tone="ok">Seal on the report = {form.seal.trim().toUpperCase()}</Pill>
          )}
          {refusal && (
            <div className="flex flex-col gap-0.5 rounded-card border-[1.5px] border-danger px-3 py-2 text-[14px] text-danger">
              {refusal.map((r) => (
                <span key={r}>{r}</span>
              ))}
            </div>
          )}
          {result && (
            <>
              <Pill tone={overallState(result.overall).tone} wrap>
                Overall: {overallState(result.overall).text}
              </Pill>
              <LabRowsTable rows={result.rows} />
            </>
          )}
        </BoardCard>
      }
      side={
        <SidePanel
          label="Report as received"
          title="Report as received"
          sub={form.pdf?.name ?? 'No PDF attached yet'}
        >
          <Note className="text-ink">
            Check the seal number on the PDF against the scanned seal.
          </Note>
          <Note>
            There is no second check: the quality person who releases the lot reads this PDF before
            signing (F7.6). A wrong report is kept; the corrected one is attached beside it.
          </Note>
          <div className="flex-1" />
          <PanelActions>
            <ActionButton
              perm="lab-reports:create"
              leadingIcon={<FloppyDisk />}
              loading={attach.isPending}
              onClick={() => void save()}
            >
              Save report
            </ActionButton>
          </PanelActions>
        </SidePanel>
      }
    />
  )
}

function ReportView({ id }: { id: string }) {
  const q = useLabReport(id)
  const r = q.data
  if (q.isLoading) return <Note>Loading the report…</Note>
  if (q.error || !r)
    return (
      <Note className="text-danger">{q.error ? errorMessage(q.error) : 'Report not found.'}</Note>
    )
  const s = overallState(r.overall)
  const panels = new Set(r.results.map((x) => x.panel)).size
  return (
    <BoardColumns
      main={
        <BoardCard title={`Lab report ${r.reportNo}`}>
          <Facts>
            <FactRow label="Sample seal">
              {r.sealPayload
                ? `${r.sealPayload} · ${r.sampleKind === 'LOT_RETAINED' ? 'gate seal' : 'QC seal'}`
                : '—'}
            </FactRow>
            <FactRow label={r.sampleKind === 'LOT_RETAINED' ? 'Farmer lot' : 'Batch'}>
              {r.unitCode}
            </FactRow>
            <FactRow label="Lab">
              {r.labName ?? '—'}
              {r.accreditationNo ? ` · ${r.accreditationNo}` : ''}
            </FactRow>
            <FactRow label="Accredited to">{boardDate(r.accreditationValidTo)}</FactRow>
            <FactRow label="Test date">{boardDate(r.testedAt)}</FactRow>
            {r.status === 'SUPERSEDED' && (
              <FactRow label="Status">Replaced by a corrected report</FactRow>
            )}
          </Facts>
          <LabRowsTable rows={r.results} />
          <Pill tone={s.tone} wrap>
            Overall: {s.text}
          </Pill>
          <Note>
            {panels} panel{panels === 1 ? '' : 's'} · {r.results.length} analytes. ‘Not detected’ is
            always shown with its LOQ, never as 0 or nil.
          </Note>
        </BoardCard>
      }
      side={
        <SidePanel
          label="Report as received"
          title="Report as received"
          sub={`${r.reportNo}.pdf · signed by the lab`}
        >
          {r.pdfUrl ? (
            <a
              href={r.pdfUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-[15px] font-bold underline"
            >
              <FilePdf aria-hidden="true" /> Open the PDF beside the rows
            </a>
          ) : (
            <Note>The PDF link could not be made; try again shortly.</Note>
          )}
          <Note className="text-ink">
            Check the seal number on the PDF against the scanned seal.
          </Note>
          <Facts>
            <FactRow label="Attached by">
              <UserName id={r.enteredById} name={r.enteredByName} />
            </FactRow>
            <FactRow label="Received">{boardWhen(r.receivedAt)}</FactRow>
          </Facts>
          <Note>
            There is no second check: the quality person who releases the lot reads this PDF before
            signing (F7.6).
          </Note>
        </SidePanel>
      }
    />
  )
}
